import { useDeferredValue, useState } from 'react';
import { Mail, Plus, Search, UserPlus } from 'lucide-react';
import { useUsersInfinite, type UserFilters } from '@/api/users';
import { useTeams } from '@/api/teams';
import type { Role, User } from '@/api/types';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Select,
  Spinner,
} from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { formatRelative, humanize } from '@/lib/format';
import { WORKSPACE_ROLES } from './helpers';
import { RoleBadge } from './shared';
import { UserFormModal } from './UserFormModal';
import { InviteModal, PendingInvites } from './InvitePeople';

export function AgentsPage() {
  const { can } = useAuth();
  const canManage = can('create', 'User');
  const [q, setQ] = useState('');
  const [role, setRole] = useState<Role | ''>('');
  const [active, setActive] = useState<'true' | 'false' | ''>('true');
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const [inviting, setInviting] = useState(false);

  const filters: UserFilters = {
    q: useDeferredValue(q.trim()),
    role,
    isActive: active === '' ? undefined : active === 'true',
  };
  const users = useUsersInfinite(filters);
  const teams = useTeams();
  const teamName = new Map((teams.data ?? []).map((t) => [t.id, t.name]));
  const rows = users.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <>
      <PageHeader
        title="Agents & roles"
        description="Who can sign in, what they can do, their skills and how much work routing may give them."
        actions={
          canManage && (
            <span className="flex gap-2">
              <Button variant="secondary" onClick={() => setEditing('new')}>
                <Plus className="h-4 w-4" /> Add with password
              </Button>
              <Button onClick={() => setInviting(true)}>
                <Mail className="h-4 w-4" /> Invite people
              </Button>
            </span>
          )
        }
      />

      <Card>
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-end">
          <label className="relative flex-1">
            <span className="sr-only">Search by name or email</span>
            <Search
              className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              className="focus:border-brand-500 focus:ring-brand-500/30 h-10 w-full rounded-lg border border-slate-200 pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
            />
          </label>
          <Select
            aria-label="Filter by role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role | '')}
            wrapperClassName="sm:w-44"
          >
            <option value="">All roles</option>
            {WORKSPACE_ROLES.map((r) => (
              <option key={r} value={r}>
                {humanize(r)}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Filter by status"
            value={active}
            onChange={(e) => setActive(e.target.value as 'true' | 'false' | '')}
            wrapperClassName="sm:w-40"
          >
            <option value="true">Active</option>
            <option value="false">Deactivated</option>
            <option value="">Everyone</option>
          </Select>
        </div>

        {users.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" label="Loading agents" />
          </div>
        ) : users.error ? (
          <ErrorState error={users.error} onRetry={() => void users.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<UserPlus className="h-6 w-6" />}
            title="No one matches these filters"
            description={
              q || role ? 'Try a different search or role.' : 'Add your first agent to start routing tickets.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  <tr className="border-b border-slate-100">
                    <th scope="col" className="px-4 py-3">
                      Name
                    </th>
                    <th scope="col" className="px-4 py-3">
                      Role
                    </th>
                    <th scope="col" className="px-4 py-3">
                      Teams
                    </th>
                    <th scope="col" className="px-4 py-3">
                      Skills
                    </th>
                    <th scope="col" className="px-4 py-3">
                      Capacity
                    </th>
                    <th scope="col" className="px-4 py-3">
                      Last login
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((u) => (
                    <tr
                      key={u.id}
                      className={canManage ? 'cursor-pointer hover:bg-slate-50' : undefined}
                      onClick={canManage ? () => setEditing(u) : undefined}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar name={u.name} src={u.avatarUrl} status={u.status} size="sm" />
                          <div className="min-w-0">
                            {canManage ? (
                              <button
                                type="button"
                                className="hover:text-brand-700 truncate font-medium text-slate-900"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditing(u);
                                }}
                              >
                                {u.name}
                              </button>
                            ) : (
                              <p className="truncate font-medium text-slate-900">{u.name}</p>
                            )}
                            <p className="truncate text-xs text-slate-500">{u.email}</p>
                          </div>
                          {!u.isActive && <Badge tone="red">Deactivated</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {u.teamIds.map((id) => teamName.get(id) ?? '…').join(', ') || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex max-w-56 flex-wrap gap-1">
                          {u.skills.length ? (
                            u.skills.map((s) => <Badge key={s}>{s}</Badge>)
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                        {u.maxConcurrentChats} chats · {u.maxOpenTickets} tickets
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-500">
                        {formatRelative(u.lastLoginAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.hasNextPage && (
              <div className="flex justify-center border-t border-slate-100 p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  loading={users.isFetchingNextPage}
                  onClick={() => void users.fetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      <PendingInvites />
      {inviting && <InviteModal onClose={() => setInviting(false)} />}

      {editing && (
        <UserFormModal
          user={editing === 'new' ? null : editing}
          teams={teams.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
